import React, { useReducer } from "react";
import {
  Form,
  Row,
  Col,
  FormControl,
  FormGroup,
  Spinner,
} from "react-bootstrap";
import { Eye, EyeSlash } from "react-bootstrap-icons";
import { Link, useNavigate } from "react-router-dom";
import { XCircleFill } from "react-bootstrap-icons";
import { AuthConsumer } from "../lib/contexts/AuthContext";

export function Login() {
  const initialState = {
    error: "",
    username: {
      value: "",
      required: true,
      touched: false,
      invalid: true,
      message: "",
    },
    password: {
      value: "",
      required: true,
      touched: false,
      invalid: true,
      message: "",
    },
    showPassword: false,
    isLoading: false,
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case "idBlurred":
        return {
          ...state,
          [action.payload]: {
            ...state[action.payload],
            touched: true,
          },
        };
      case "idChanged":
        return {
          ...state,
          [action.payload.id]: {
            ...state[action.payload],
            value: action.payload.value,
          },
        };
      case "invalidateFields":
        return {
          ...state,
          [action.payload.id]: {
            ...state[action.payload.id],
            invalid: action.payload.invalid,
            message: action.payload.message,
          },
        };
      case "showPassword":
        return {
          ...state,
          showPassword: action.payload,
        };
      case "error":
        return {
          ...state,
          error: action.payload,
          isLoading: false,
        };
      case "isLoading":
        return {
          ...state,
          isLoading: action.payload,
        };
      default:
        throw new Error();
    }
  };

  const [loginState, dispatch] = useReducer(reducer, initialState);
  const navigate = useNavigate();
  const privacyPolicy = "https://clearpointtrust.com/privacy-policy/";

  const handleBlur = (event) => {
    const { id } = event.target;
    dispatch({ type: "idBlurred", payload: id });
  };

  const handleChange = (event) => {
    const { id, value } = event.target;
    dispatch({ type: "idChanged", payload: { id, value } });
  };

  const shouldMarkError = (id) => {
    const field = loginState[id];
    const result = field.value === "" && field.touched;
    return result;
  };

  const formIsValid = () => {
    var invalidFields = Object.keys(loginState).filter(
      (field) =>
        loginState[field].hasOwnProperty("invalid") &&
        loginState[field].value === "",
    );
    return invalidFields.length === 0;
  };

  const toggleShowPassword = () => {
    dispatch({ type: "showPassword", payload: !loginState.showPassword });
  };

  const redirectToHome = () => {
    navigate("/home");
  };

  const showError = (error) => {
    dispatch({ type: "error", payload: error });
  };

  const submit = (authContext) => (event) => {
    event.preventDefault();
    dispatch({ type: "isLoading", payload: true });
    const { username, password } = loginState;
    if (formIsValid()) {
      authContext.login(
        username.value,
        password.value,
        redirectToHome,
        showError,
      );
    }
  };

  const { error, username, password, showPassword, isLoading } = loginState;

  return (
    <AuthConsumer>
      {(context) => (
        <div className="panel panel-body panel-default sm-panel">
          <Form onSubmit={submit(context)} noValidate>
            <div className="header section">
              WELCOME TO THE CLEARPOINT PORTAL
            </div>
            {error !== "" && (
              <div className="alert alert-danger section" role="alert">
                <span className="me-1" aria-hidden="true">
                  {<XCircleFill />}
                </span>
                <span className="sr-only">Error:</span>
                {" " + error}
              </div>
            )}

            <Row className="border-between section">
              <Col sm={12}>
                <FormGroup className="field">
                  <Form.Label htmlFor="username">Username</Form.Label>
                  <FormControl
                    type="text"
                    id="username"
                    name="username"
                    placeholder="Enter your username"
                    value={username.value}
                    onBlur={handleBlur}
                    isInvalid={shouldMarkError("username") ? " error" : null}
                    onChange={handleChange}
                  />
                  <FormControl.Feedback />
                </FormGroup>

                <FormGroup className="field">
                  <Form.Label htmlFor="password">Password</Form.Label>
                  <FormControl
                    type={showPassword ? "text" : "password"}
                    style={{ display: "inline-block" }}
                    id="password"
                    name="password"
                    placeholder="Enter your password"
                    autoComplete="on"
                    value={password.value}
                    onBlur={handleBlur}
                    isInvalid={shouldMarkError("password") ? " error" : null}
                    onChange={handleChange}
                  />
                  <FormControl.Feedback />
                  {!showPassword && password.value !== "" && (
                    <Eye
                      style={{ marginLeft: "-20px", cursor: "pointer" }}
                      onClick={toggleShowPassword}
                    />
                  )}
                  {showPassword && (
                    <EyeSlash
                      style={{ marginLeft: "-20px", cursor: "pointer" }}
                      onClick={toggleShowPassword}
                    />
                  )}
                </FormGroup>

                <FormGroup className="mb-0">
                  {isLoading ? (
                    <Spinner animation="border" role="status" variant="primary">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  ) : (
                    <button
                      className="btn btn-primary login-btn"
                      onSubmit={submit(context)}
                      disabled={!formIsValid()}
                    >
                      Log in
                    </button>
                  )}
                </FormGroup>
              </Col>
            </Row>

            <Row className="text-sm text-start login-links">
              <Col className="me-auto">
                Don't have an account yet? <Link to={"/signup"}>Sign Up</Link>
              </Col>
              <Col className="ms-auto text-end">
                <Link to={"/forgot-password"}>
                  Forgot Password/First Time Login
                </Link>
              </Col>
            </Row>

            <Row>&nbsp;</Row>
            <Row>
              <h5>Your privacy is our responsibility.</h5>
            </Row>
            <Row>
              <div className="text-sm">
                We promise to keep your personal information private and secure.
                To learn more, please read our{" "}
                <a
                  target="_blank"
                  href={privacyPolicy}
                  rel="noopener noreferrer"
                >
                  privacy policy
                </a>
                .
              </div>
            </Row>
          </Form>
        </div>
      )}
    </AuthConsumer>
  );
}
